import React, { FC, useState, useEffect, useRef } from 'react';
import { useAdvisorChat } from '../../hooks/useAdvisorChat';
import type { FinancialContext } from '../../types/chat';
import { DebugSection } from '../debug/DebugLayout';

interface AIChatHistoryProps {
  context: FinancialContext;
}

// Common questions that loan advisors might ask - shortened for UI space
const SUGGESTED_PROMPTS = [
  'Loan eligibility?',
  'Payment history concerns?',
  'DTI impact on approval?',
  'Cash reserves status?',
  'Areas needing improvement?',
  'LCU Relationship?',
  'Employment History?',
  'NCUA advice?',
];

export const AIChatHistory: FC<AIChatHistoryProps> = ({ context }) => {
  const { messages, isLoading, error, sendMessage, resetChat } = useAdvisorChat(context);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Store the current scroll position before updating
  useEffect(() => {
    scrollPositionRef.current = window.scrollY;
  }, [messages]);

  // Update scroll behavior to scroll only the messages container
  useEffect(() => {
    if (messagesEndRef.current && chatContainerRef.current) {
      // Only scroll the chat container, not the window
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

      // Restore the window scroll position
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: 'auto',
      });
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!inputValue.trim() || isLoading) return;

    // Store current scroll position
    scrollPositionRef.current = window.scrollY;

    await sendMessage(inputValue.trim());
    setInputValue('');

    // Restore scroll position
    window.scrollTo({
      top: scrollPositionRef.current,
      behavior: 'auto',
    });
  };

  // Handle clicking a suggested prompt
  const handlePromptClick = async (prompt: string) => {
    if (isLoading) return;

    // Store current scroll position
    scrollPositionRef.current = window.scrollY;

    // Map short prompts to full questions for the bot
    const fullPrompts: Record<string, string> = {
      'Loan eligibility?': "What's this member's loan eligibility based on their VeraScore?",
      'Payment history concerns?': 'Are there any concerning trends in their payment history?',
      'DTI impact on approval?': 'How does their debt-to-income ratio affect approval chances?',
      'Cash reserves status?': "What's their cash reserve situation for handling emergencies?",
      'Areas needing improvement?': 'Which financial factors need the most improvement?',
      'LCU Relationship?':
        "What's this member's relationship with our credit union? How long have they been a member?",
      'Employment History?':
        "Can you provide a summary of this member's employment history over the last 2 years?",
      'NCUA advice?':
        "What NCUA lending guidelines should we consider for this member's situation?",
    };

    // Get the full prompt text
    const fullPromptText = fullPrompts[prompt] || prompt;
    
    // Send the full prompt text
    await sendMessage(fullPromptText);

    // Restore scroll position
    window.scrollTo({
      top: scrollPositionRef.current,
      behavior: 'auto',
    });
  };

  // Handle clearing the chat
  const handleClearChat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Store current scroll position
    scrollPositionRef.current = window.scrollY;

    // Reset the chat
    resetChat();

    // Restore scroll position
    window.scrollTo({
      top: scrollPositionRef.current,
      behavior: 'auto',
    });
  };

  // Group prompts into pairs for better layout
  const promptPairs = [];
  for (let i = 0; i < SUGGESTED_PROMPTS.length; i += 2) {
    promptPairs.push(SUGGESTED_PROMPTS.slice(i, i + 2));
  }

  return (
    <div 
      className="flex flex-col h-[700px] overflow-hidden rounded-xl relative"
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
      }}
    >
      {/* Chat Header - Auto height */}
      <DebugSection label="Section C-2-1: Chat Header">
        <div 
          className="p-3 rounded-t-xl flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            borderBottom: '1px solid rgba(79, 70, 229, 0.3)',
            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.15)'
          }}
        >
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-sm text-white">VeraScore Advisor</h3>
              <p className="text-xs text-white/80">AI-powered financial guidance</p>
            </div>
            <button
              onClick={handleClearChat}
              disabled={isLoading || messages.length === 0}
              className="px-2 py-1 text-xs rounded transition-colors text-white"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
            >
              Clear Chat
            </button>
          </div>
        </div>
      </DebugSection>

      {/* Messages Area - Takes remaining space with its own scrollbar */}
      <DebugSection label="Section C-2-2: Chat Messages" className="flex-grow overflow-hidden">
        <div
          ref={chatContainerRef}
          className="h-full overflow-y-auto p-3 space-y-3"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E1 transparent',
            background: 'linear-gradient(to bottom, rgba(249, 250, 251, 0.7), rgba(248, 250, 252, 0.7))'
          }}
        >
          {messages.map(message => (
            <div
              key={message.id}
              className={`flex items-start space-x-2 ${
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              {message.role === 'assistant' && (
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(to bottom right, #8b5cf6, #7c3aed)',
                    boxShadow: '0 1px 2px rgba(124, 58, 237, 0.3)'
                  }}
                >
                  <span className="text-white text-xs">VA</span>
                </div>
              )}
              <div
                className="rounded-lg p-2 max-w-[85%]"
                style={message.role === 'user' 
                  ? {
                      background: 'linear-gradient(135deg, #4f86f1, #2563eb)',
                      boxShadow: '0 2px 4px rgba(37, 99, 235, 0.3)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      color: 'white',
                      fontWeight: 500
                    }
                  : {
                      background: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.9))',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      border: '1px solid rgba(203, 213, 225, 0.3)',
                      color: '#334155'
                    }
                }
              >
                <p className="text-xs">{message.content}</p>
              </div>
            </div>
          ))}

          {/* Suggested Questions - shown after initial message */}
          {messages.length > 0 && (
            <div className="ml-8 mt-2">
              <div className="flex flex-wrap justify-between gap-y-1.5 max-w-[85%]">
                {promptPairs.map((pair, pairIndex) => (
                  <React.Fragment key={`pair-${pairIndex}`}>
                    {pair.map((prompt, index) => {
                      return (
                        <button
                          key={`${pairIndex}-${index}`}
                          onClick={e => {
                            e.preventDefault();
                            handlePromptClick(prompt);
                          }}
                          disabled={isLoading}
                          className="w-[48%] text-xs p-1.5 rounded-md text-left transition-colors"
                          style={{
                            background: 'linear-gradient(to bottom, rgba(238, 242, 255, 0.7), rgba(224, 231, 255, 0.7))',
                            border: '1px solid rgba(199, 210, 254, 0.4)',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            color: '#4338ca',
                            backdropFilter: 'blur(4px)',
                            WebkitBackdropFilter: 'blur(4px)'
                          }}
                        >
                          {prompt}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Reference to scroll to the bottom */}
          <div ref={messagesEndRef} />

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-center">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center mr-2"
                style={{
                  background: 'linear-gradient(to bottom right, #8b5cf6, #7c3aed)',
                  boxShadow: '0 1px 2px rgba(124, 58, 237, 0.3)'
                }}
              >
                <span className="text-white text-xs">VA</span>
              </div>
              <div 
                className="rounded-lg p-2 bg-slate-100 text-slate-700"
                style={{
                  background: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.9))',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  border: '1px solid rgba(203, 213, 225, 0.3)'
                }}
              >
                <div className="flex space-x-1">
                  <div className="h-1.5 w-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
                  <div
                    className="h-1.5 w-1.5 bg-indigo-500 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                  <div
                    className="h-1.5 w-1.5 bg-indigo-500 rounded-full animate-bounce"
                    style={{ animationDelay: '0.4s' }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div 
              className="text-xs text-red-500 p-2 rounded mt-2"
              style={{
                background: 'linear-gradient(to bottom, rgba(254, 226, 226, 0.7), rgba(254, 202, 202, 0.7))',
                border: '1px solid rgba(252, 165, 165, 0.3)'
              }}
            >
              Error: {error}
            </div>
          )}
        </div>
      </DebugSection>

      {/* Chat Input - Fixed height, stays at bottom */}
      <DebugSection label="Section C-2-3: Chat Input">
        <div 
          className="p-3 flex-shrink-0 border-t"
          style={{
            background: 'linear-gradient(to bottom, rgba(248, 250, 252, 0.7), rgba(241, 245, 249, 0.7))',
            borderTop: '1px solid rgba(203, 213, 225, 0.3)'
          }}
        >
          <form onSubmit={handleSubmit} className="flex space-x-2">
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder="Ask a question about this member..."
              className="flex-grow text-xs py-2 px-3 rounded-md"
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(203, 213, 225, 0.5)',
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="px-3 py-2 rounded-md text-white text-xs font-medium transition-colors"
              style={{
                background: inputValue.trim() && !isLoading 
                  ? 'linear-gradient(to bottom right, #4f46e5, #4338ca)' 
                  : 'linear-gradient(to bottom, #9ca3af, #6b7280)',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                border: inputValue.trim() && !isLoading
                  ? '1px solid rgba(79, 70, 229, 0.3)'
                  : '1px solid rgba(156, 163, 175, 0.3)'
              }}
            >
              Send
            </button>
          </form>
        </div>
      </DebugSection>
    </div>
  );
};
